import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Play, CheckCircle, Edit2, Printer, Trash2, XCircle, RefreshCcw, Clock, Building2, Layers, X, Wrench } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const jobStatusColors = {
  'Not Started': 'bg-slate-100 text-slate-700',
  'In Progress': 'bg-blue-50 text-blue-700',
  'Received': 'bg-emerald-50 text-emerald-700',
  'Cancelled': 'bg-red-50 text-red-700',
  'Partial Cancelled': 'bg-orange-50 text-orange-700',
  'Corrected': 'bg-purple-50 text-purple-700',
};

function getJobStatus(job) {
  if (job.status === 'cancelled') return 'Cancelled';
  if (job.status === 'partial_cancelled') return 'Partial Cancelled';
  if (job.status === 'corrected') return 'Corrected';
  const issued = job.quantity_issued || 0;
  const completed = job.quantity_completed || 0;
  if (completed >= issued && issued > 0) return 'Received';
  if (issued > 0) return 'In Progress';
  return 'Not Started';
}

export default function ProcessDetailView({
  workOrder: wo,
  process,
  onBack,
  user,
  // Actions
  onOpenIssueDialog,
  onOpenReceiveDialog,
  onOpenEditBPDDialog,
  onDeleteBPD,
  onOpenEditJobDialog,
  onOpenCancelJobDialog,
  onOpenCorrectJobDialog,
  onOpenReworkJobDialog,
  onOpenSkipProcess,
  onPrintJob,
}) {
  const [expandedPooledJob, setExpandedPooledJob] = useState(null);
  const [pooledJobLoading, setPooledJobLoading] = useState(false);

  // Fetch pooled job details when clicking the indicator
  const fetchPooledJobDetails = async (pooledJobId, pooledJobNumber) => {
    if (expandedPooledJob?.id === pooledJobId) {
      setExpandedPooledJob(null);
      return;
    }
    
    setPooledJobLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/pooled-jobs/${pooledJobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpandedPooledJob(res.data);
    } catch (err) {
      console.error('Failed to fetch pooled job:', err);
      // Set minimal data so we can show something
      setExpandedPooledJob({ id: pooledJobId, pooled_job_number: pooledJobNumber, line_items: [] });
    } finally {
      setPooledJobLoading(false);
    }
  };

  // Calculate days remaining
  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    try {
      const deliveryDate = new Date(dateStr);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deliveryDate.setHours(0, 0, 0, 0);
      const diffTime = deliveryDate - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  if (!wo || !process) return null;

  const jobs = process.jobs || [];
  const totalIssued = jobs.reduce((s, j) => s + (j.quantity_issued || 0), 0);
  const totalCompleted = jobs.reduce((s, j) => s + (j.quantity_completed || 0), 0);
  const totalDefects = jobs.reduce((s, j) => s + (j.quantity_defects || 0), 0);
  const progressPct = wo.quantity > 0 ? Math.min(100, Math.round((totalCompleted / wo.quantity) * 100)) : 0;
  const pendingQty = wo.quantity - totalIssued;

  const isWOCompleted = wo.status === 'closed' || wo.status === 'received' || wo.status === 'completed' || wo.is_closed;
  const isProcessCompleted = totalCompleted >= wo.quantity || (process.completed_quantity || 0) >= wo.quantity;
  const canCreateJob = pendingQty > 0 && process.status !== 'cancelled' && process.status !== 'skipped' && !isWOCompleted && !isProcessCompleted;
  const canSkip = process.status === 'not_started' && jobs.length === 0 && !isWOCompleted;

  return (
    <div className="space-y-5">
      {/* Back + Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-0.5 shrink-0" data-testid="process-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to {wo.work_order_number}
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold tracking-tight">{process.process_name}</h2>
            {process.is_outsourced && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700"><Building2 className="w-3 h-3 mr-0.5" /> Outsourced</Badge>}
            {process.is_adhoc && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Ad-hoc</Badge>}
            {process.status === 'skipped' && <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs">Skipped</Badge>}
            {process.pooled_job_number && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Pooled: {process.pooled_job_number}</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {wo.work_order_number} &middot; {wo.item_name}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canSkip && (
            <Button size="sm" variant="outline" className="text-orange-600 hover:bg-orange-50" onClick={() => onOpenSkipProcess(wo, process)}>
              <XCircle className="w-3.5 h-3.5 mr-1" /> Skip Process
            </Button>
          )}
          {canCreateJob && (
            <Button size="sm" onClick={() => onOpenIssueDialog(wo, process)} data-testid="new-job-btn">
              <Play className="w-3.5 h-3.5 mr-1" /> New Job
            </Button>
          )}
        </div>
      </div>

      {/* Process summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Target Time</p>
          <p className="text-sm font-semibold mt-0.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {process.target_time_hours}h</p>
        </div>
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Issued</p>
          <p className="text-sm font-semibold mt-0.5">{totalIssued} / {wo.quantity}</p>
        </div>
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Completed</p>
          <p className="text-sm font-semibold mt-0.5">{totalCompleted} / {wo.quantity}</p>
          <Progress value={progressPct} className="h-1.5 mt-1" />
        </div>
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Defects</p>
          <p className={`text-sm font-semibold mt-0.5 ${totalDefects > 0 ? 'text-red-600' : ''}`}>{totalDefects}</p>
        </div>
        <div className="bg-muted/30 rounded-lg px-4 py-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending to Issue</p>
          <p className="text-sm font-semibold mt-0.5">{Math.max(0, pendingQty)}</p>
        </div>
      </div>

      {/* Job Cards Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-[80px]">Job</TableHead>
                <TableHead>Workers</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead className="text-center">Issued Qty</TableHead>
                <TableHead className="text-center">Completed</TableHead>
                <TableHead className="text-center">Defects</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => {
                const jobStatus = getJobStatus(job);
                const canReceive = (job.quantity_issued || 0) > (job.quantity_completed || 0) && job.status !== 'cancelled' && job.status !== 'partial_cancelled' && job.status !== 'corrected';
                const canAddBPD = (job.quantity_issued || 0) > 0 && job.status !== 'cancelled' && job.status !== 'partial_cancelled' && job.status !== 'corrected';
                const canEditJob = job.status !== 'cancelled' && job.status !== 'partial_cancelled' && job.status !== 'corrected';
                const isJobFullyReceived = (job.quantity_completed || 0) >= (job.quantity_issued || 0) && (job.quantity_issued || 0) > 0;
                const canCancelJob = job.status !== 'cancelled' && job.status !== 'partial_cancelled' && job.status !== 'corrected' && !isJobFullyReceived && (user?.allowed_modules?.includes('work_orders') || user?.is_owner);
                const canCorrectJob = job.status !== 'cancelled' && job.status !== 'partial_cancelled' && job.status !== 'corrected' && !isJobFullyReceived && (job.quantity_issued || 0) > 0 && (user?.allowed_modules?.includes('work_orders') || user?.is_owner);
                const canRework = (job.quantity_defects || 0) > 0 && job.status !== 'cancelled' && job.status !== 'corrected' && !isWOCompleted;
                const jobBPDs = (wo.bpd_items || []).map((bpd, idx) => ({ ...bpd, originalIndex: idx })).filter(b => b.job_id === job.id);

                return (
                  <React.Fragment key={job.id}>
                    <TableRow className={`${job.status === 'cancelled' ? 'bg-red-50/30 opacity-60' : job.is_rework ? 'bg-amber-50/30' : ''}`}
                      data-testid={`job-row-${job.job_letter}`}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold">Job {job.job_letter}</span>
                          {job.is_rework && <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-50 text-amber-700">Rework #{job.rework_cycle}</Badge>}
                          {process.pooled_job_id && (
                            <button
                              onClick={() => fetchPooledJobDetails(process.pooled_job_id, process.pooled_job_number)}
                              className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                                expandedPooledJob?.id === process.pooled_job_id
                                  ? 'bg-purple-100 text-purple-800 border-purple-300'
                                  : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                              }`}
                              data-testid={`pooled-job-indicator-${job.job_letter}`}
                            >
                              <Layers className="w-3 h-3" />
                              {process.pooled_job_number}
                            </button>
                          )}
                        </div>
                        {job.job_number && <p className="text-xs text-muted-foreground">{job.job_number}</p>}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {job.workers?.length > 0
                            ? job.workers.map(w => w.employee_name || w.worker_name || '').join(', ')
                            : <span className="text-muted-foreground">-</span>}
                        </div>
                        {job.issued_by_name && <p className="text-xs text-muted-foreground">Issued by: {job.issued_by_name}</p>}
                      </TableCell>
                      <TableCell className="text-sm">{job.machine || '-'}</TableCell>
                      <TableCell className="text-center font-medium">{job.quantity_issued || 0}</TableCell>
                      <TableCell className="text-center font-medium">{job.quantity_completed || 0}</TableCell>
                      <TableCell className="text-center">
                        {(job.quantity_defects || 0) > 0
                          ? <span className="text-red-600 font-medium">{job.quantity_defects}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${jobStatusColors[jobStatus]}`}>{jobStatus}</Badge>
                        {job.quality_passed === false && <Badge variant="destructive" className="ml-1 text-xs">QC Fail</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          {canEditJob && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onOpenEditJobDialog(wo, process, job)}
                              title="Edit Job" data-testid={`edit-job-${job.job_letter}`}>
                              <Edit2 className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                          )}
                          {(job.quantity_issued || 0) > 0 && job.status !== 'cancelled' && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onPrintJob(job, process, wo)}
                              title="Print Job Card" data-testid={`print-job-${job.job_letter}`}>
                              <Printer className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          )}
                          {canCancelJob && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onOpenCancelJobDialog(wo, process, job)}
                              title="Cancel Job" data-testid={`cancel-job-${job.job_letter}`}>
                              <XCircle className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          )}
                          {canCorrectJob && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onOpenCorrectJobDialog(wo, process, job)}
                              title="Correct Job" data-testid={`correct-job-${job.job_letter}`}>
                              <Wrench className="w-3.5 h-3.5 text-amber-600" />
                            </Button>
                          )}
                          {canReceive && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpenReceiveDialog(wo, process, job)}
                              data-testid={`receive-job-${job.job_letter}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Receive
                            </Button>
                          )}
                          {canRework && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={() => onOpenReworkJobDialog(wo, process, job)} title={`Rework ${job.quantity_defects} defects`}>
                              <RefreshCcw className="w-3 h-3 mr-1" /> Rework
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* BPD items for this job */}
                    {jobBPDs.map((bpd) => (
                      <TableRow key={bpd.originalIndex} className="bg-amber-50/30 text-xs">
                        <TableCell className="pl-8 text-amber-600">BPD</TableCell>
                        <TableCell colSpan={2}>
                          {bpd.item_name} &mdash; Qty: {bpd.quantity_received} {bpd.unit} @ ₹{bpd.price}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-center text-emerald-600 font-medium">₹{(bpd.amount || 0).toFixed(2)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onOpenEditBPDDialog(wo, bpd, bpd.originalIndex)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => onDeleteBPD(wo.id, bpd.originalIndex)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Pooled Job breakdown (expanded view) */}
                    {process.pooled_job_id && expandedPooledJob?.id === process.pooled_job_id && (
                      <TableRow className="bg-purple-50/50">
                        <TableCell colSpan={8} className="p-0">
                          <div className="border-l-4 border-purple-400 m-2 rounded-r bg-white shadow-sm">
                            <div className="flex items-center justify-between px-3 py-2 bg-purple-100/50 border-b">
                              <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4 text-purple-600" />
                                <span className="font-semibold text-purple-800">Pooled Job: {expandedPooledJob.pooled_job_number}</span>
                                <span className="text-sm text-purple-600">
                                  {expandedPooledJob.process_name} • Total: {expandedPooledJob.total_quantity} units • {expandedPooledJob.status}
                                </span>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-purple-200"
                                onClick={() => setExpandedPooledJob(null)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            {pooledJobLoading ? (
                              <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-50">
                                    <tr>
                                      <th className="text-left px-3 py-2 font-medium text-slate-600">SO Line</th>
                                      <th className="text-left px-3 py-2 font-medium text-slate-600">WO Number</th>
                                      <th className="text-left px-3 py-2 font-medium text-slate-600">Item</th>
                                      <th className="text-left px-3 py-2 font-medium text-slate-600">Customer</th>
                                      <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                                      <th className="text-left px-3 py-2 font-medium text-slate-600">Delivery</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(expandedPooledJob.line_items || []).map((li, idx) => {
                                      const daysLeft = getDaysRemaining(li.delivery_date);
                                      const isCurrentWO = li.work_order_id === wo.id;
                                      // Display SO line code: use so_line_code for standard WOs, 
                                      // or show count for multi-SO WOs with so_allocations
                                      const soDisplay = li.so_line_code 
                                        ? li.so_line_code 
                                        : (li.so_allocations && li.so_allocations.length > 0)
                                          ? `${li.so_allocations[0]?.so_line_code || li.so_allocations[0]?.so_number}${li.so_allocations.length > 1 ? ` +${li.so_allocations.length - 1}` : ''}`
                                          : (li.so_number || '-');
                                      return (
                                        <tr 
                                          key={li.work_order_id || idx} 
                                          className={`border-t ${isCurrentWO ? 'bg-purple-100/50 font-medium' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                                        >
                                          <td className="px-3 py-2 font-medium text-slate-800">
                                            {soDisplay}
                                          </td>
                                          <td className="px-3 py-2">
                                            {li.work_order_number}
                                            {isCurrentWO && <span className="ml-1 text-xs text-purple-600">(current)</span>}
                                          </td>
                                          <td className="px-3 py-2 text-slate-600">{li.item_name}</td>
                                          <td className="px-3 py-2 text-slate-600">{li.customer_name || '-'}</td>
                                          <td className="px-3 py-2 text-right">{li.quantity_to_produce}</td>
                                          <td className="px-3 py-2">
                                            {li.delivery_date ? (
                                              <span className="flex items-center gap-1">
                                                <span className="text-slate-600">{formatDate(li.delivery_date)}</span>
                                                {daysLeft !== null && (
                                                  <span className={`text-xs font-medium ${daysLeft <= 7 ? 'text-red-600' : daysLeft <= 14 ? 'text-amber-600' : 'text-green-600'}`}>
                                                    ({daysLeft}d)
                                                  </span>
                                                )}
                                              </span>
                                            ) : '-'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {(expandedPooledJob.line_items || []).length === 0 && (
                                  <div className="p-4 text-center text-sm text-muted-foreground">No line items found</div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          {jobs.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p>No jobs created yet.</p>
              {canCreateJob && <p className="text-sm mt-1">Click "New Job" to issue this process.</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
